import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { DataTable } from '@/components/data/DataTable'
import { useMyAssignments, useMyAssignmentLocationOptions, useMyAssignmentTeamOptions, useMyAssignmentRecoverySetOptions, useMyAssignmentStats } from '../hooks'

import { createRecoverRequestColumns } from '../columns'
import { useUnassignEngineer, useStartRecovery, useMarkComplete } from '@/features/recover-requests/hooks'
import { recoverRequestsApi } from '@/features/recover-requests/api'
import { useToast } from '@/hooks/use-toast'
import { UnassignConfirmDialog } from '../components/UnassignConfirmDialog'
import { StartRecoveryConfirmDialog } from '../components/StartRecoveryConfirmDialog'
import { MarkCompleteConfirmDialog } from '../components/MarkCompleteConfirmDialog'
import { JsonViewDialog } from '@/components/ui/JsonViewDialog'
import { RunbookViewDialog } from '@/features/recover-requests/components/RunbookViewDialog'
import { RecoverRequestDetailsDialog } from '@/features/recover-requests/components/RecoverRequestDetailsDialog'
import { ObjectQRCodeDialog } from '@/features/objects/components/ObjectQRCodeDialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { X, FileText, Package, MapPin } from 'lucide-react'
import type { MyAssignment } from '../api'
import { debounce } from '@/lib/utils'
import { useCan } from '@/hooks/useCan'

export function MyAssignmentsPage() {
    const { t } = useTranslation()
    const { i18n } = useTranslation()
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(20)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [locationFilter, setLocationFilter] = useState<string>('')
    const [teamFilter, setTeamFilter] = useState<string>('')
    const [recoverySetFilter, setRecoverySetFilter] = useState<string>('')
    const [jsonDialog, setJsonDialog] = useState<{ title: string; content: string | null } | null>(null)
    const [runbookDialog, setRunbookDialog] = useState<{ objectID: string; scenario: number | null; forecastStartTime: string | Date | null } | null>(null)
    const [detailsDialog, setDetailsDialog] = useState<{ objectID: string; requestID: string } | null>(null)
    const [showQRCodeDialog, setShowQRCodeDialog] = useState(false)
    const [qrCodeObjectID, setQrCodeObjectID] = useState<string | null>(null)
    const [unassignDialog, setUnassignDialog] = useState<{ objectID: string; objectDescription: string | null; recoverRequestId: number | null } | null>(null)
    const [startRecoveryDialog, setStartRecoveryDialog] = useState<{
        objectID: string
        objectDescription: string | null
        requestID: string | null
        scenario: number | null
        forecastStartTime: string | Date | null
    } | null>(null)

    const [markCompleteDialog, setMarkCompleteDialog] = useState<{ objectID: string; objectDescription: string | null; recoverRequestId: number | null } | null>(null)

    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const unassign = useUnassignEngineer()
    const startRecovery = useStartRecovery()
    const markComplete = useMarkComplete()
    // Start, mark complete and unassign are the engineer's actions: recovery.execute (the server
    // also checks the assignment) or a manager's recover-requests.update. Not offered without either.
    const canExecute = useCan('recovery.execute', 'recover-requests.update')
    // Debounce Object ID search so we don't refetch on every keystroke
    const debouncedSetSearch = useMemo(() => debounce((value: string) => setDebouncedSearch(value), 350), [])
    // A search still waiting when the page closes is dropped, not applied to a page that is gone.
    useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

    useEffect(() => {
        debouncedSetSearch(search)
    }, [search, debouncedSetSearch])

    const { data: myAssignmentsResponse } = useMyAssignments({
        page,
        perPage: limit,
        search: debouncedSearch || undefined,
        location: locationFilter || undefined,
        team: teamFilter || undefined,
        recoverySet: recoverySetFilter || undefined,
    })

    // The backend embeds stats and filter options in the list response — no extra API calls
    const responseData = myAssignmentsResponse?.data as any
    const myAssignments: MyAssignment[] = useMemo(() => {
        const dataArray = responseData?.data
        if (!dataArray || !Array.isArray(dataArray)) return []
        return dataArray
    }, [responseData])

    const stats = useMyAssignmentStats(responseData)
    const locationOptions = useMyAssignmentLocationOptions(responseData)
    const teamOptions = useMyAssignmentTeamOptions(responseData)
    const recoverySetOptions = useMyAssignmentRecoverySetOptions()

    const handleShowMetadata = useCallback((title: string, content: string | null) => {
        setJsonDialog({ title, content })
    }, [])

    const handleShowRunbook = useCallback((objectID: string, scenario: number | null, forecastStartTime: string | Date | null) => {
        setRunbookDialog({ objectID, scenario, forecastStartTime })
    }, [])

    const handleShowFloorPlan = useCallback((objectID: string) => {
        // TODO: Implement floor plan view
        console.log('Show floor plan for:', objectID)
    }, [])

    // No assign engineer functionality for my assignments
    const handleAssignEngineer = useCallback(() => {
        // Not applicable for my assignments
    }, [])

    const handleShowObjectDetails = useCallback((objectID: string, requestID: string) => {
        setDetailsDialog({ objectID, requestID })
    }, [])

    const handleShowQRCode = useCallback((objectID: string) => {
        setQrCodeObjectID(objectID)
        setShowQRCodeDialog(true)
    }, [])

    const handleUnassign = useCallback((objectID: string, objectDescription: string | null, recoverRequestId: number | null) => {
        setUnassignDialog({ objectID, objectDescription, recoverRequestId })
    }, [])

    const handleStartRecovery = useCallback((
        objectID: string,
        objectDescription: string | null,
        _recoverRequestId: number | null,
        scenario: number | null,
        forecastStartTime: string | Date | null,
        requestID: string | null
    ) => {
        setStartRecoveryDialog({ objectID, objectDescription, requestID, scenario, forecastStartTime })
    }, [])

    const handleConfirmStartRecovery = useCallback(() => {
        if (!startRecoveryDialog || !startRecoveryDialog.requestID || !startRecoveryDialog.objectID || startRecoveryDialog.scenario == null) {
            return
        }
        const requestID = Number(startRecoveryDialog.requestID)
        if (isNaN(requestID)) return

        startRecovery.mutate(
            {
                requestID,
                objectID: startRecoveryDialog.objectID,
                scenario: startRecoveryDialog.scenario,
            },
            {
                onSuccess: async () => {
                    setStartRecoveryDialog(null)
                    await queryClient.prefetchQuery({
                        queryKey: ['recovery-runbook', requestID],
                        queryFn: async () => {
                            const response = await recoverRequestsApi.getRecoveryRunbook(requestID)
                            return response.data?.data || []
                        },
                    })
                    navigate(`/recovery-runbook/${requestID}`, {
                        state: {
                            objectID: startRecoveryDialog.objectID,
                            objectDescription: startRecoveryDialog.objectDescription,
                            forecastStartTime: startRecoveryDialog.forecastStartTime,
                            from: '/my-assignments',
                        },
                    })
                },
                onError: () => {
                    // Keep dialog open on error
                },
            }
        )
    }, [startRecoveryDialog, startRecovery, navigate, queryClient])

    const handleConfirmUnassign = useCallback(() => {
        if (!unassignDialog || unassignDialog.recoverRequestId == null) {
            console.warn('🔍 DEBUG handleConfirmUnassign: Missing dialog or recoverRequestId', { unassignDialog })
            return
        }
        console.log('🔍 DEBUG handleConfirmUnassign: Calling unassign mutation', {
            requestID: unassignDialog.recoverRequestId,
            objectID: unassignDialog.objectID
        })
        unassign.mutate(
            { requestID: unassignDialog.recoverRequestId },
            {
                onSuccess: () => {
                    console.log('🔍 DEBUG handleConfirmUnassign: Success, closing dialog')
                    setUnassignDialog(null)
                },
                onError: (error) => {
                    console.error('🔍 DEBUG handleConfirmUnassign: Error', error)
                    // Keep dialog open on error so user can see the error message
                },
                onSettled: () => {
                    // Dialog will be closed in onSuccess or stay open on error
                }
            }
        )
    }, [unassignDialog, unassign])

    const handleMarkComplete = useCallback((objectID: string, objectDescription: string | null, recoverRequestId: number | null) => {
        setMarkCompleteDialog({ objectID, objectDescription, recoverRequestId })
    }, [])

    const handleConfirmMarkComplete = useCallback(() => {
        if (!markCompleteDialog || markCompleteDialog.recoverRequestId == null) return
        markComplete.mutate(
            { requestID: markCompleteDialog.recoverRequestId },
            {
                onSuccess: () => {
                    setMarkCompleteDialog(null)
                },
            }
        )
    }, [markCompleteDialog, markComplete])

    const columns = useMemo(
        () => createRecoverRequestColumns(
            t, i18n.language, handleShowMetadata, handleShowRunbook, handleShowFloorPlan, undefined, handleShowObjectDetails, handleShowQRCode,
            canExecute ? handleUnassign : undefined,
            canExecute ? handleStartRecovery : undefined,
            canExecute ? handleMarkComplete : undefined,
        ),
        [t, i18n.language, handleShowMetadata, handleShowRunbook, handleShowFloorPlan, handleShowObjectDetails, handleShowQRCode, handleUnassign, handleStartRecovery, handleMarkComplete, canExecute]
    )

    const handleExport = useCallback(() => {
        console.log('Export my assignments to CSV')
    }, [])


    const handleClearFilters = useCallback(() => {
        setLocationFilter('')
        setTeamFilter('')
        setRecoverySetFilter('')
        setSearch('')
        setPage(1)
    }, [])

    return (
        <div className="space-y-[var(--spacing-section)]" style={{ padding: 'var(--spacing-page)' }}>
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl lg:text-2xl font-bold">{t('myAssignments.title')}</h1>
                    <p className="text-muted-foreground">
                        {t('myAssignments.description')}
                    </p>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-lg border bg-card p-6">
                    <div className="flex items-center space-x-2">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium">{t('myAssignments.numberRequests')}</p>
                            <p className="text-2xl font-bold">
                                {stats.uniqueRequestCount}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="rounded-lg border bg-card p-6">
                    <div className="flex items-center space-x-2">
                        <Package className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium">{t('myAssignments.numberObjects')}</p>
                            <p className="text-2xl font-bold">
                                {stats.objectCount}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="rounded-lg border bg-card p-6">
                    <div className="flex items-center space-x-2">
                        <MapPin className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium">{t('myAssignments.numberLocations')}</p>
                            <p className="text-2xl font-bold">
                                {stats.locationCount}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div
                className="overflow-x-auto [&_td]:!py-1"
                style={{
                    marginLeft: 'calc(var(--spacing-page) * -2)',
                    marginRight: 'calc(var(--spacing-page) * -2)',
                    paddingLeft: 'calc(var(--spacing-page) * 0.05)',
                    paddingRight: 'calc(var(--spacing-page) * 0.05)',
                    width: 'calc(100vw - var(--sidebar-width-expanded) - var(--spacing-page) * 0.1)',
                    maxWidth: 'none',
                    minWidth: '1400px',
                }}
            >
                <DataTable
                    columns={columns}
                    data={myAssignments}
                    getCellClassName={(row: MyAssignment, columnId: string) => (columnId === 'objectID' ? (row.waitGo?.toUpperCase() === 'GO' ? 'border-l-4 border-l-[rgb(151,200,100)]' : 'border-l-4 border-l-transparent') : '')}
                    firstHeaderCellClassName="border-l-4 border-l-[hsl(var(--table-header))]"
                    searchKey="objectID"
                    searchPlaceholder={t('myAssignments.searchObjectID')}
                    searchValue={search}
                    onSearchChange={(value) => {
                        setSearch(value)
                        setPage(1)
                    }}
                    onExport={handleExport}
                    exportButtonText={t('myAssignments.export')}
                    enableRowSelection={false}
                    pagination={{
                        page,
                        limit,
                        total: myAssignmentsResponse?.data?.total || 0,
                        onPageChange: setPage,
                        onPageSizeChange: setLimit,
                    }}
                    toolbarContent={
                        <div className="flex items-center gap-2 ml-4 flex-nowrap">
                            {/* Location Filter */}
                            <div className="w-[180px] flex-shrink-0">
                                <Select value={locationFilter || 'all'} onValueChange={(value) => {
                                    setLocationFilter(value === 'all' ? '' : value)
                                    setPage(1)
                                }}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('myAssignments.filterLocation')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('common.all')}</SelectItem>
                                        {locationOptions?.map((location) => (
                                            <SelectItem key={location} value={location}>
                                                {location}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Team Filter */}
                            <div className="w-[180px] flex-shrink-0">
                                <Select value={teamFilter || 'all'} onValueChange={(value) => {
                                    setTeamFilter(value === 'all' ? '' : value)
                                    setPage(1)
                                }}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('myAssignments.filterTeam')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('common.all')}</SelectItem>
                                        {teamOptions?.map((team: string) => (
                                            <SelectItem key={team} value={team}>
                                                {team}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Recovery Set Filter */}
                            <div className="w-[180px] flex-shrink-0">
                                <Select value={recoverySetFilter || 'all'} onValueChange={(value) => {
                                    setRecoverySetFilter(value === 'all' ? '' : value)
                                    setPage(1)
                                }}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('myAssignments.filterRecoverySet')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('common.all')}</SelectItem>
                                        {recoverySetOptions?.map((set) => (
                                            <SelectItem key={set} value={set}>
                                                {set}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Clear Filters Button */}
                            {(locationFilter || teamFilter || recoverySetFilter || search) && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleClearFilters}
                                    className="gap-2 flex-shrink-0"
                                >
                                    <X className="h-4 w-4" />
                                    {t('myAssignments.clearFilters')}
                                </Button>
                            )}
                        </div>
                    }
                />
            </div>

            {/* JSON View Dialog */}
            {jsonDialog && (
                <JsonViewDialog
                    open={!!jsonDialog}
                    onOpenChange={(open) => {
                        if (!open) setJsonDialog(null)
                    }}
                    title={jsonDialog.title}
                    content={jsonDialog.content}
                />
            )}

            {/* Runbook View Dialog */}
            {runbookDialog && (
                <RunbookViewDialog
                    open={!!runbookDialog}
                    onOpenChange={(open) => {
                        if (!open) setRunbookDialog(null)
                    }}
                    objectID={runbookDialog.objectID}
                    scenario={runbookDialog.scenario}
                    forecastStartTime={runbookDialog.forecastStartTime}
                />
            )}

            {/* Recover Request Details Dialog */}
            {detailsDialog && (
                <RecoverRequestDetailsDialog
                    open={!!detailsDialog}
                    onOpenChange={(open) => {
                        if (!open) setDetailsDialog(null)
                    }}
                    objectID={detailsDialog.objectID}
                    requestID={detailsDialog.requestID}
                />
            )}

            {/* QR Code Dialog */}
            <ObjectQRCodeDialog
                open={showQRCodeDialog}
                onOpenChange={(open) => {
                    setShowQRCodeDialog(open)
                    if (!open) setQrCodeObjectID(null)
                }}
                objectID={qrCodeObjectID}
            />

            {/* Unassign Confirm Dialog */}
            {unassignDialog && (
                <UnassignConfirmDialog
                    open={!!unassignDialog}
                    onOpenChange={(open) => {
                        if (!open) setUnassignDialog(null)
                    }}
                    objectID={unassignDialog.objectID}
                    objectDescription={unassignDialog.objectDescription}
                    recoverRequestId={unassignDialog.recoverRequestId}
                    onConfirm={handleConfirmUnassign}
                    isLoading={unassign.isPending}
                />
            )}

            {/* Start Recovery Confirm Dialog */}
            {startRecoveryDialog && (
                <StartRecoveryConfirmDialog
                    open={!!startRecoveryDialog}
                    onOpenChange={(open) => {
                        if (!open) setStartRecoveryDialog(null)
                    }}
                    objectID={startRecoveryDialog.objectID}
                    objectDescription={startRecoveryDialog.objectDescription}
                    onConfirm={handleConfirmStartRecovery}
                    isLoading={startRecovery.isPending}
                />
            )}

            {/* Mark Complete Confirm Dialog */}
            {markCompleteDialog && (
                <MarkCompleteConfirmDialog
                    open={!!markCompleteDialog}
                    onOpenChange={(open) => {
                        if (!open) setMarkCompleteDialog(null)
                    }}
                    objectID={markCompleteDialog.objectID}
                    objectDescription={markCompleteDialog.objectDescription}
                    onConfirm={handleConfirmMarkComplete}
                    isLoading={markComplete.isPending}
                />
            )}
        </div>
    )
}
